/**
 * @Author: Zero
 * @Date: 2026-07-24 11:08:27
 * @Explain: 消息推送 - 推送实验
 */
import http from '../http';
import httpv2 from '../http-v2';
// type
import type {
  AbcContentResult,
  AiGenResult,
  ApproveConfigParams,
  CheckExpNameParams,
  CheckExpNameResult,
  CheckJsonParams,
  CheckJsonV2Params,
  CheckJsonV2Result,
  ConfigCountParams,
  CreateExpConfigParams,
  CreateExpConfigResult,
  CreateExpConfigV2Params,
  DownloadConfigParams,
  FinishExpParams,
  GetDefaultConfigParams,
  GetOperationLogParams,
  GetUserLangResult,
  PendingConfigListParams,
  PushApiResponse,
  SearchConfigListParams,
  SearchExpListParams,
  UpdateConfigStatusParams,
  UpdateDefaultConfigParams,
  ViewConfigItemParams,
  ViewConfigParams,
  ViewPlayerCountParams,
} from './data.d';

const MODEL = 'push_notification';

export default {
  // 暂由配置 pushGameIdWhitelist 代替：获取业务信息(带有业务名称、支持按照业务名称搜索)
  getGameInfoList(data: Record<any, any>) {
    return http.post(`/business/all`, data, {
      params: { isPackage: 'N' },
    });
  },
  // 查看默认推送配置
  getDefaultConfig(data: GetDefaultConfigParams) {
    return http.post(`${MODEL}/default_config`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 默认推送变更记录
  getOperationLog(data: GetOperationLogParams) {
    return http.post(`${MODEL}/default_operation_log`, data, {
      params: { switchReqToUnderline: true },
    });
  },

  // 下载配置详情（后端返回 JSON 数据，前端自行序列化为文件下载）
  // 注意：此接口返回的 data 为配置原文（snake_case），需保持原样下载，
  // 因此不开启 switchReqToUnderline，避免 key 被自动转换为 camelCase
  downloadConfig(data: DownloadConfigParams) {
    return http.post(`${MODEL}/download_config`, data, {
      params: { isPackage: 'N' },
    });
  },

  // 预览配置项
  viewConfigItem(data: ViewConfigItemParams) {
    return http.post(`${MODEL}/view_config_item`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 查看待审核配置详情
  // 注意：响应中 exp_config_detail.push_content 的 key 为真实分组名，
  // 若开启统一 camelCase 转换会破坏分组 key，因此这里手动拼 snake_case。
  viewConfig(data: ViewConfigParams) {
    const payload = {
      game_id: data.gameId,
      config_id: data.configId,
      config_type: data.configType,
    };
    return http.post(`${MODEL}/view_config`, payload, {
      params: { isPackage: 'N' },
    });
  },

  // 暂停/重启默认推送
  updateConfigStatus(data: UpdateConfigStatusParams) {
    return http.post(`${MODEL}/update_config_status`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 更新默认推送配置（在“更改默认推送配置”弹窗中点击「发布配置」时调用）
  updateDefaultConfig(data: UpdateDefaultConfigParams) {
    return http.post(`${MODEL}/update_default_config`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 搜索可选配置列表
  searchConfigList(data: SearchConfigListParams) {
    return http.post(`${MODEL}/config_list`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 搜索实验列表
  // 注意：push_content 包含业务分组 key（如 Treatment_A），必须保持原样，
  // 因此 value 不做驼峰转换。
  searchExpList(data: SearchExpListParams) {
    return http.post(`${MODEL}/exp_list`, data, {
      params: {
        switchReqToUnderline: true,
        isPackage: 'N',
        ignoreConvertedValueKey: JSON.stringify(['push_content']),
      },
    });
  },

  // 审批中数量
  configCount(data: ConfigCountParams) {
    return httpv2.post(`${MODEL}/config_count`, data, {
      params: { isPackage: 'N', switchReqToUnderline: true },
    });
  },

  // 待审核列表
  pendingConfigList(data: PendingConfigListParams) {
    return http.post(`${MODEL}/pending_config_list`, data, {
      params: { switchReqToUnderline: true },
    });
  },

  // 配置审批（通过 / 驳回）
  approveConfig(data: ApproveConfigParams) {
    return http.post(`${MODEL}/approve_config`, data, {
      params: { isPackage: 'N', switchReqToUnderline: true },
    });
  },

  // 结束实验
  finishExp(data: FinishExpParams) {
    return http.post(`${MODEL}/finish_exp`, data, {
      params: { switchReqToUnderline: true, isPackage: 'N' },
    });
  },

  // 查询可推送玩家数量
  viewPlayerCount(data: ViewPlayerCountParams) {
    return http.post(`${MODEL}/view_player_count`, data, {
      params: { isPackage: 'N', switchReqToUnderline: true },
    });
  },

  // 校验实验配置（名称重复 / 实验正在运行 等）
  checkJson(data: CheckJsonParams) {
    const payload = {
      game_id: data.gameId,
      config_name: data.configName,
      config_start_date: data.configStartDate,
      config_end_date: data.configEndDate,
      config_json: data.configJson,
      default_lang: data.defaultLang,
    };
    return http.post(`${MODEL}/check_json`, payload, {
      params: { isPackage: 'N' },
    });
  },

  // 上传 CSV / 内容后校验并回填 config_details
  checkJsonV2(
    data: CheckJsonV2Params,
  ): Promise<PushApiResponse<CheckJsonV2Result>> {
    const payload = {
      game_id: data.gameId,
      config_name: data.configName,
      config_start_date: data.configStartDate,
      config_end_date: data.configEndDate,
      config_json: data.configJson,
      is_push_id_gen: data.isPushIdGen,
      action_type: data.actionType,
      ...(typeof data.configId === 'number' && data.configId > 0
        ? { config_id: data.configId }
        : {}),
    };
    return http.post<CheckJsonV2Result, PushApiResponse<CheckJsonV2Result>>(
      `${MODEL}/check_json`,
      payload,
      { params: { isPackage: 'N' } },
    );
  },

  // 新建实验推送
  createExpConfig(data: CreateExpConfigParams) {
    const payload = {
      game_id: data.gameId,
      config_name: data.configName,
      config_start_date: data.configStartDate,
      config_end_date: data.configEndDate,
      // 分组名属于业务 key，必须原样透传，不能走统一下划线转换
      config_details: data.configDetails,
      config_json: data.configJson,
      default_lang: data.defaultLang,
    };
    return http.post(`${MODEL}/create_exp_config`, payload, {
      params: { isPackage: 'N' },
    });
  },

  // 新建/编辑实验配置
  createExpConfigV2(
    data: CreateExpConfigV2Params,
  ): Promise<PushApiResponse<CreateExpConfigResult>> {
    const payload = {
      game_id: data.gameId,
      config_name: data.configName,
      config_start_date: data.configStartDate,
      config_end_date: data.configEndDate,
      // 分组 key 属于业务标识，必须原样透传，不能走统一下划线转换
      config_details: data.configDetails,
      config_json: data.configJson,
      create_type: data.createType,
      is_draft: data.isDraft,
      action_type: data.actionType,
      // 语言与分组快照（顶层附加字段 config_detail，snake_case 原样透传）
      ...(data.configDetail ? { config_detail: data.configDetail } : {}),
      ...(typeof data.configId === 'number' && data.configId > 0
        ? { config_id: data.configId }
        : {}),
    };
    return http.post<
      CreateExpConfigResult,
      PushApiResponse<CreateExpConfigResult>
    >(`${MODEL}/create_exp_config`, payload, {
      params: { isPackage: 'N' },
    });
  },

  // 检查push name
  checkExpName(
    data: CheckExpNameParams,
  ): Promise<PushApiResponse<CheckExpNameResult>> {
    const payload = {
      configId: data.configId,
      configName: data.configName,
    };
    return httpv2.post<CheckExpNameResult, PushApiResponse<CheckExpNameResult>>(
      `${MODEL}/check_exp_name`,
      payload,
      {
        params: { isPackage: 'N', switchReqToUnderline: true },
      },
    );
  },

  // 获取用户语言 - 无需传参
  getUserLang(): Promise<PushApiResponse<GetUserLangResult>> {
    return http.post<GetUserLangResult, PushApiResponse<GetUserLangResult>>(
      `${MODEL}/get_user_lang`,
    );
  },

  // 获取abc内容（实验分组）
  getAbcContent(): Promise<PushApiResponse<AbcContentResult>> {
    return http.post<AbcContentResult, PushApiResponse<AbcContentResult>>(
      `${MODEL}/get_abc_list`,
    );
  },

  // AI 文案生成（Databricks push-copy-generator 经网关透传，submit / status 共用本端点）
  getGenAiContent(
    data?: Record<string, any>,
  ): Promise<PushApiResponse<AiGenResult>> {
    return http.post<AiGenResult, PushApiResponse<AiGenResult>>(
      `${MODEL}/gen_ai_content`,
      // 网关要求最外层为 data（isPackage='N' 不做统一包装，这里显式包一层）
      data ? { data } : {},
      { params: { isPackage: 'N', unUseCommonMsg: true } },
    );
  },

  // 创建配置
  createExpContent() {
    return http.post(`${MODEL}/create_exp_content`);
  },

  // 检查json文件
  checkJsonFile() {
    return http.post(`${MODEL}/check_json`);
  },
};
